"use client";

import React, { useCallback, useEffect, useId, useRef, useState } from 'react';

export type TooltipProps = {
  /** The tooltip text. Keep it short — this is a hint, not documentation. */
  content: React.ReactNode;
  children: React.ReactElement;
  side?: 'top' | 'bottom';
  className?: string;
};

const SIDE_CLASSES = {
  top: 'bottom-full left-1/2 -translate-x-1/2 mb-1.5',
  bottom: 'top-full left-1/2 -translate-x-1/2 mt-1.5',
} as const;

/**
 * Hint attached to a control.
 *
 * Opens on focus as well as hover, which is what makes it reachable by keyboard, and
 * Escape dismisses it while focus stays put. `aria-describedby` rather than
 * `aria-label` so the trigger keeps its own accessible name.
 *
 * A tooltip is never the only place information lives — it is unavailable on touch,
 * where there is no hover. Anything a decision depends on belongs on the surface
 * itself (catalog principle 01).
 */
export function Tooltip({ content, children, side = 'top', className = '' }: TooltipProps) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const wrapperRef = useRef<HTMLSpanElement>(null);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, close]);

  return (
    <span
      ref={wrapperRef}
      className="relative inline-flex"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={close}
      onFocusCapture={() => setOpen(true)}
      onBlurCapture={close}
    >
      {React.cloneElement(children, { 'aria-describedby': open ? id : undefined } as Record<string, unknown>)}
      {open ? (
        <span
          id={id}
          role="tooltip"
          className={`pointer-events-none absolute z-overlay w-max max-w-64 rounded-control border border-line bg-surface px-2 py-1 text-xs leading-5 text-content shadow-e2 ${SIDE_CLASSES[side]} ${className}`}
        >
          {content}
        </span>
      ) : null}
    </span>
  );
}

export default Tooltip;
